import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';
import theme from './theme/theme';
import { AuthProvider } from './contexts/AuthContext';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import CookieBanner from './components/shared/CookieBanner';
import LegalAcceptanceGuard from './components/shared/LegalAcceptanceGuard';
import ErrorBoundary from './components/shared/ErrorBoundary';

// Global styles & i18n
import './global.css';
import './i18n';

/**
 * Query defaults: staleTime 5min (listings tolerate staleness; freshness-
 * critical pages override, e.g. partner scan flow uses staleTime: 0);
 * retry 1 (avoid amplifying load on a struggling backend);
 * refetchOnWindowFocus false (avoids surprise refetches on chat-widget
 * focus-toggle; opt in per query). ChatWidget deliberately bypasses
 * react-query — it's WebSocket-based with its own polling fallback.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// eslint-disable-next-line xss/no-mixed-html -- standard React 18 root attach point; createRoot accepts a DOM element, not raw HTML.
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

/**
 * ErrorBoundary sits below ThemeProvider (fallback UI needs theme +
 * CssBaseline) but above BrowserRouter+AuthProvider so auth-bootstrap
 * crashes still show the recoverable fallback, not a blank screen.
 */
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            {/* Wraps auth + app so the banner renders over any route and
                consent state is readable anywhere. */}
            <CookieConsentProvider>
              <AuthProvider>
                {/* Blocks signed-in users with pending T&C/Privacy
                    acceptances until they accept. */}
                <LegalAcceptanceGuard>
                  <App />
                </LegalAcceptanceGuard>
                <CookieBanner />
              </AuthProvider>
            </CookieConsentProvider>
          </QueryClientProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </ThemeProvider>
  </React.StrictMode>
);
